import { Box, useToken } from 'native-base';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
} from 'react-native';

const styles = StyleSheet.create({
  input: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingTop: 24,
    paddingBottom: 8,
    fontSize: 14,
  },
});

export type TextInputProps = RNTextInputProps & {
  /** Floating label text */
  label?: string;
  children?: ReactNode;
  /** Maps to editable=false */
  isReadOnly?: boolean;
  /** Maps to editable=false */
  isDisabled?: boolean;
  /** Show asterisk on label */
  isRequired?: boolean;
  /** Show invalid styling */
  isInvalid?: boolean;
  /** "password" maps to secureTextEntry */
  type?: string;
  /** Ignored — kept for backward compat */
  variant?: string;
};

type TextInputFocusHandler = NonNullable<RNTextInputProps['onFocus']>;
type TextInputBlurHandler = NonNullable<RNTextInputProps['onBlur']>;

export default forwardRef<RNTextInput, TextInputProps>(function TextInput(
  {
    label,
    placeholder,
    value,
    onFocus,
    onBlur,
    children,
    isReadOnly,
    isDisabled,
    isRequired,
    isInvalid,
    type,
    variant: _variant,
    ...inputProps
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const labelAnim = useRef(new Animated.Value(value ? 1 : 0)).current;
  const [gray300, muted300, primary600, errorColor] = useToken('colors', [
    'gray.300',
    'muted.300',
    'primary.600',
    'brand.error',
  ]);

  useEffect(() => {
    const toValue = isFocused || !!value ? 1 : 0;
    Animated.timing(labelAnim, {
      toValue,
      duration: 200,
      easing: Easing.ease,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [labelAnim, isFocused, value]);

  const handleFocus: TextInputFocusHandler = useCallback(
    (e: Parameters<TextInputFocusHandler>[0]) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur: TextInputBlurHandler = useCallback(
    (e: Parameters<TextInputBlurHandler>[0]) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const labelStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      left: 12,
      color: gray300,
      zIndex: 1,
      fontSize: isFocused || !!value ? 10 : 14,
      transform: [
        {
          translateY: labelAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [16, 8],
          }),
        },
      ],
    }),
    [gray300, isFocused, labelAnim, value]
  );

  const editable = !(isReadOnly || isDisabled);
  let borderColor = muted300;
  if (isFocused) borderColor = primary600;
  if (isInvalid) borderColor = errorColor;

  return (
    <Box position="relative" width="100%">
      <Animated.Text style={labelStyle}>
        {label || placeholder}
        {isRequired && '*'}
      </Animated.Text>

      <RNTextInput
        ref={ref}
        {...inputProps}
        editable={editable}
        secureTextEntry={type === 'password'}
        style={[
          styles.input,
          {
            borderColor,
            fontFamily: 'ProximaNova',
          },
        ]}
        value={value}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />

      {children}
    </Box>
  );
});
